import { useEffect, useState } from "react";
import Modal from "../ui/Modal";
import { updateCompany } from "../../api/companies";
import { SCompanyOut, SCompanyUpdate } from "../../types/companies";
import { BedDouble, Building2, CalendarDays, FileText, Link2, MapPin, UsersRound } from "lucide-react";
import { STANDARD_RU_CITIES } from "../../constants/cities";

type Props = {
  open: boolean;
  onClose: () => void;
  company: SCompanyOut | null;
  onUpdated: () => void;
};

export default function EditCompanyModal({ open, onClose, company, onUpdated }: Props) {
  const [name, setName] = useState("");
  const [inn, setInn] = useState("");
  const [city, setCity] = useState("");
  const [competitorsCount, setCompetitorsCount] = useState("5");
  const [objectsLimit, setObjectsLimit] = useState("1");
  const [otaCount, setOtaCount] = useState("3");
  const [subscriptionExpiresOn, setSubscriptionExpiresOn] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const cityListId = "company-edit-city-suggestions";
  const baseInputClass =
    "w-full rounded-2xl border border-slate-200/80 bg-white/80 px-4 py-[14px] text-[15px] text-slate-900 placeholder:text-slate-400 shadow-inner transition focus:border-indigo-300 focus:ring-4 focus:ring-indigo-200/60 disabled:opacity-60 disabled:cursor-not-allowed";

  useEffect(() => {
    if (open && company) {
      setName(company.company_name || "");
      setInn(company.inn || "");
      setCity(company.city || "");
      setCompetitorsCount(String(company.competitors_count ?? 5));
      setObjectsLimit(String(company.objects_limit ?? 1));
      setOtaCount(String(company.ota_count ?? 3));
      setSubscriptionExpiresOn(company.subscription_expires_on || "");
      setError("");
    }
  }, [open, company]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!name.trim()) return setError("Введите название компании");
    if (inn && !/^\d+$/.test(inn)) return setError("ИНН может содержать только цифры");
    if (!/^\d+$/.test(competitorsCount.trim())) return setError("Укажите целое число конкурентов");
    if (!/^\d+$/.test(objectsLimit.trim())) return setError("Укажите целое число для лимита объектов");
    if (!/^\d+$/.test(otaCount.trim())) return setError("Укажите целое число для количества ОТА");
    if (Number(objectsLimit) < 1) return setError("Лимит объектов должен быть не меньше 1");
    if (Number(otaCount) < 1) return setError("Количество ОТА должно быть не меньше 1");

    setSubmitting(true);
    try {
      const payload: SCompanyUpdate = {
        company_name: name.trim(),
        inn: inn.trim() || null,
        city: city.trim() || null,
        competitors_count: Number(competitorsCount),
        objects_limit: Number(objectsLimit),
        ota_count: Number(otaCount),
        subscription_expires_on: subscriptionExpiresOn || null,
      };
      if (company) {
        await updateCompany(company.id, payload);
      }
      onUpdated();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Не удалось обновить компанию");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="" widthClass="max-w-lg">
      <form onSubmit={onSubmit} className="space-y-6">
        <div className="text-center space-y-3 animate-softFade">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-600 shadow-inner">
            <Building2 size={24} />
          </div>
          <div>
            <h3 className="text-2xl font-semibold text-slate-900">Редактировать компанию</h3>
            <p className="mt-1 text-sm text-slate-500">Обновите реквизиты и географию для корректной отчётности</p>
          </div>
        </div>

        <label className="block space-y-2">
          <span className="text-sm font-semibold text-slate-600">Название компании *</span>
          <div className="relative">
            <Building2 size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className={`${baseInputClass} pl-11 pr-4`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ООО «ТревелСтат»"
              disabled={submitting}
            />
          </div>
        </label>

        <label className="block space-y-2">
          <span className="text-sm font-semibold text-slate-600">ИНН</span>
          <div className="relative">
            <FileText size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className={`${baseInputClass} pl-11 pr-4`}
              value={inn}
              onChange={(e) => setInn(e.target.value)}
              placeholder="1234567890"
              disabled={submitting}
            />
          </div>
        </label>

        <label className="block space-y-2">
          <span className="text-sm font-semibold text-slate-600">Город</span>
          <div className="relative">
            <MapPin size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className={`${baseInputClass} pl-11 pr-4`}
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Москва"
              list={cityListId}
              disabled={submitting}
            />
          </div>
          <datalist id={cityListId}>
            {STANDARD_RU_CITIES.map((cityOption) => (
              <option key={cityOption} value={cityOption} />
            ))}
          </datalist>
        </label>

        <label className="block space-y-2">
          <span className="text-sm font-semibold text-slate-600">Кол-во конкурентов на объект размещения</span>
          <div className="relative">
            <UsersRound size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="number"
              min={0}
              step={1}
              className={`${baseInputClass} pl-11 pr-4`}
              value={competitorsCount}
              onChange={(e) => setCompetitorsCount(e.target.value)}
              placeholder="5"
              disabled={submitting}
            />
          </div>
        </label>

        <label className="block space-y-2">
          <span className="text-sm font-semibold text-slate-600">Макс. количество объектов размещения</span>
          <div className="relative">
            <BedDouble size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="number"
              min={1}
              step={1}
              className={`${baseInputClass} pl-11 pr-4`}
              value={objectsLimit}
              onChange={(e) => setObjectsLimit(e.target.value)}
              placeholder="1"
              disabled={submitting}
            />
          </div>
        </label>

        <label className="block space-y-2">
          <span className="text-sm font-semibold text-slate-600">Кол-во ОТА по умолчанию</span>
          <div className="relative">
            <Link2 size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="number"
              min={1}
              step={1}
              className={`${baseInputClass} pl-11 pr-4`}
              value={otaCount}
              onChange={(e) => setOtaCount(e.target.value)}
              placeholder="3"
              disabled={submitting}
            />
          </div>
        </label>

        <label className="block space-y-2">
          <span className="text-sm font-semibold text-slate-600">Подписка действует до</span>
          <div className="relative">
            <CalendarDays size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="date"
              className={`${baseInputClass} pl-11 pr-4`}
              value={subscriptionExpiresOn}
              onChange={(e) => setSubscriptionExpiresOn(e.target.value)}
              disabled={submitting}
            />
          </div>
        </label>

        {error && (
          <div className="animate-softFade rounded-2xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-800 shadow-inner">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-center">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex flex-1 items-center justify-center rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_18px_30px_rgba(99,102,241,0.35)] transition hover:translate-y-[-1px] hover:brightness-105 disabled:opacity-50 sm:flex-none"
          >
            {submitting ? "Сохраняем…" : "Сохранить"}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex flex-1 items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50 sm:flex-none"
          >
            Отмена
          </button>
        </div>
      </form>
    </Modal>
  );
}
